import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  Paper,
  Fade,
  Zoom,
} from '@mui/material';
import {
  QrCodeIcon,
  ArrowDownTrayIcon as DownloadIcon,
  ShareIcon,
  PrinterIcon,
  XMarkIcon,
  UserIcon,
  EyeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector } from '../store';
import { config } from '../config/environment';
import { apiService } from '../services/api';
import QRCode from 'qrcode';
import { printHTML } from '../utils/printUtils';
import { generateQRLayout, generateWorkerQRLayout } from '../utils/qrLayoutGenerator';

interface UserQRCodeProps {
  userId: string;
  userName: string;
  userEmail: string;
  variant?: 'button' | 'card' | 'inline';
  size?: 'small' | 'medium' | 'large';
  showProfileUrl?: boolean;
}

interface QRCodeData {
  qrCode: string;
  qrImage: {
    data: string;
    type: string;
  };
  qrData: string;
  type: string;
  profileUrl?: string;
}

const UserQRCode: React.FC<UserQRCodeProps> = ({
  userId,
  userName,
  userEmail,
  variant = 'button',
  size = 'medium',
  showProfileUrl = true,
}) => {
  const [open, setOpen] = useState(false);
  const [qrData, setQrData] = useState<QRCodeData | null>(null);
  const [workerQRData, setWorkerQRData] = useState<any | null>(null);
  const [qrCodeImages, setQrCodeImages] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const token = useAppSelector((state) => state.auth.token);

  useEffect(() => {
    console.log("qrCodeImages:", qrCodeImages);
  },[qrCodeImages])

  const generateQRCode = async () => {
    if (!token) {
      setError('Authentication required');
      return;
    }

    if (!userId) {
      setError('User ID is required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Try to fetch QR code using new API endpoint first
      try {
        const workerResult = await apiService.getWorkerQRCode(userId);
        if (workerResult.success && workerResult.data) {
          setWorkerQRData(workerResult.data);
          
          // Generate QR code image from qr_code string
          if (workerResult.data.worker?.qr_code) {
            const qrImage = await QRCode.toDataURL(workerResult.data.worker.qrCode, {
              width: 400,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              },
              errorCorrectionLevel: 'H'
            });
            setQrCodeImages({
              [userId]: qrImage
            });
            
            // Also set qrData for backward compatibility
            setQrData({
              qrCode: workerResult.data.worker.qrCode,
              qrData: workerResult.data.worker.qrCode,
              qrImage: {
                data: qrImage.split(',')[1], // Extract base64 data
                type: 'image/png'
              },
              type: 'worker'
            });
            setSuccess(true);
            return;
          } else if (workerResult.data.worker?.qrImageData
) {
            // Use existing QR image data if available
            setQrCodeImages({
              [userId]: workerResult.data.worker.qrImageData

            });
            setQrData({
              qrCode: workerResult.data.worker.qrCode || '',
              qrData: workerResult.data.worker.qrCode || '',
              qrImage: {
                data: workerResult.data.worker.qrImageData.split(',')[1] || '',
                type: 'image/png'
              },
              type: 'worker'
            });
            setSuccess(true);
            return;
          }
        }
      } catch (workerError) {
        console.warn('Worker QR API failed, falling back to old method:', workerError);
      }

      // Fallback to old QR generation method
      const endpoint = showProfileUrl 
        ? `${config.API_URL}/qr/generate/user/profile`
        : `${config.API_URL}/qr/generate/user`;
      
      const requestBody = showProfileUrl
        ? {
            userId,
            baseUrl: window.location.origin,
          }
        : { userId };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.success) {
        setQrData(result.data);
        setSuccess(true);
      } else {
        setError(result.error?.message || 'Failed to generate QR code');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('QR generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // Always generate QR code when opening modal
    generateQRCode();
  };

  const handleClose = () => {
    setOpen(false);
    setQrData(null);
    setError(null);
    setSuccess(false);
  };

  const handleDownload = () => {
    if (!qrData?.qrImage?.data) return;

    const link = document.createElement('a');
    link.href = `data:${qrData.qrImage.type};base64,${qrData.qrImage.data}`;
    link.download = `${userName.replace(/\s+/g, '_')}_QR_Code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = async () => {
    console.log("workerQRData:", workerQRData)
        // Use new API endpoint data if available
    if (workerQRData?.worker) {
      try {
        // Format worker data
        const workerData = {
          id: workerQRData.worker.id,
          firstName: workerQRData.worker.firstName,
          lastName: workerQRData.worker.lastName,
          email: workerQRData.worker.email,
          employeeId: workerQRData.worker.employeeId,
          role: workerQRData.worker.role,
          branch: workerQRData.worker.branch,
          stages: workerQRData.worker.stages
        };

        // Use qrCodeImages if available, otherwise generate from qr_code string
        let printQRImages = qrCodeImages;
        // if (!printQRImages[userId] && workerQRData.worker.qr_code) {
        //   const qrImage = await QRCode.toDataURL(workerQRData.worker.qr_code, {
        //     width: 240,
        //     margin: 2,
        //     color: {
        //       dark: '#000000',
        //       light: '#FFFFFF'
        //     },
        //     errorCorrectionLevel: 'H'
        //   });
        //   printQRImages = {
        //     [userId]: qrImage
        //   };
        // } else if (!printQRImages[userId] && workerQRData.worker.qr_image_data) {
        //   printQRImages = {
        //     [userId]: workerQRData.worker.qr_image_data
        //   };
        // }

        // Create a simple worker QR layout HTML using the updated generateWorkerQRLayout
        const html = generateWorkerQRLayout({
          worker: workerData,
          qrCodeImages: printQRImages,
          qrData,
        });


        printHTML({
          title: `QR - ${userName}`,
          pageSize: "50mm 25mm",
          content: html,
          singlePage: true,
        });

        return;
      } catch (err) {
        console.error('Error printing worker QR code:', err);
      }
    }

    // Fallback to old print method
    if (!qrData?.qrImage?.data) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - ${userName}</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                text-align: center;
                padding: 40px 20px;
                background: #f8fafc;
                margin: 0;
              }
              .container {
                max-width: 500px;
                margin: 0 auto;
                background: white;
                border-radius: 16px;
                padding: 40px;
                box-shadow: 0 10px 40px rgba(0,0,0,0.1);
              }
              .header {
                margin-bottom: 30px;
              }
              .qr-container {
                margin: 30px 0;
                padding: 20px;
                background: #f8fafc;
                border-radius: 12px;
                border: 2px dashed #e2e8f0;
              }
              .qr-image {
                width: 280px;
                height: 280px;
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.1);
              }
              .user-info {
                margin: 20px 0;
                padding: 20px;
                background: #f1f5f9;
                border-radius: 8px;
              }
              .user-name {
                font-size: 14px;
                font-weight: 600;
                color: #1e293b;
                margin: 0 0 8px 0;
              }
              .user-email {
                font-size: 14px;
                color: #64748b;
                  margin: 0 0 8px 0;
              }
              .footer {
                margin-top: 30px;
                font-size: 14px;
                color: #64748b;
              }
            </style>
          </head>
          <body>
            <div class="container">


              <div class="user-info">
                <div class="user-name">${userName}</div>
                <div class="user-email">${userEmail}</div>
              </div>

              <div class="qr-container">
                <img src="data:${qrData.qrImage.type};base64,${qrData.qrImage.data}"
                     alt="QR Code" class="qr-image" />
              </div>


            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleShare = async () => {
    if (!qrData?.profileUrl) {
      // Fallback to copying QR data
      try {
        await navigator.clipboard.writeText(qrData?.qrData || '');
        alert('QR code data copied to clipboard');
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${userName} - QR Code`,
          text: `Scan this QR code to view ${userName}'s profile`,
          url: qrData.profileUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback to copying URL
      try {
        await navigator.clipboard.writeText(qrData.profileUrl);
        alert('Profile URL copied to clipboard');
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const getButtonSize = () => {
    switch (size) {
      case 'small': return 'small';
      case 'large': return 'large';
      default: return 'medium';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small': return 16;
      case 'large': return 24;
      default: return 20;
    }
  };

  const renderButton = () => (
    <Button
      variant="contained"
      startIcon={<QrCodeIcon width={getIconSize()} height={getIconSize()} />}
      onClick={handleOpen}
      size={getButtonSize()}
      disabled={loading}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 500,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        '&:hover': {
          boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
        }
      }}
    >
      {loading ? 'Generating...' : 'View QR Code'}
    </Button>
  );

  const renderCard = () => (
    <Card sx={{ 
      maxWidth: 320, 
      mx: 'auto',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white'
    }}>
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Box sx={{ 
          p: 2, 
          bgcolor: 'rgba(255,255,255,0.2)', 
          borderRadius: 2,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2
        }}>
          <QrCodeIcon width={32} height={32} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          User QR Code
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mb: 3 }}>
          {userName}
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpen}
          disabled={loading}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)',
            }
          }}
        >
          {loading ? 'Generating...' : 'Generate QR'}
        </Button>
      </CardContent>
    </Card>
  );

  const renderInline = () => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title="Generate QR Code">
        <IconButton 
          onClick={handleOpen} 
          disabled={loading} 
          size="small"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            }
          }}
        >
          {loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <QrCodeIcon width={16} height={16} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderContent = () => {
    switch (variant) {
      case 'card': return renderCard();
      case 'inline': return renderInline();
      default: return renderButton();
    }
  };

  return (
    <>
      {renderContent()}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 2,
          pt: 3,
          px: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'rgba(255,255,255,0.2)',
          }
        }}>
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <QrCodeIcon width={28} height={28} />
          </Box>
 
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={handleClose} 
            size="small"
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
              }
            }}
          >
            <XMarkIcon width={20} height={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {error && (
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="error" 
                sx={{ 
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.95rem'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Error generating QR code
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {error}
                </Typography>
              </Alert>
            </Box>
          )}

          {loading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              py: 6,
              px: 3
            }}>
              <Box sx={{ 
                p: 3, 
                bgcolor: 'grey.50', 
                borderRadius: '50%',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CircularProgress size={40} thickness={4} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Generating QR Code...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we create your personalized QR code
              </Typography>
            </Box>
          )}

          {qrData && !loading && (
            <Fade in={true} timeout={500}>
              <Box sx={{ p: 3 }}>
                {/* Success Message */}
                {success && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 3,
                    p: 2,
                    bgcolor: 'success.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.200'
                  }}>
                    <CheckCircleIcon width={20} height={20} color="#10b981" />
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      QR Code generated successfully!
                    </Typography>
                  </Box>
                )}

                {/* QR Code Display */}
                <Box sx={{ 
                  textAlign: 'center', 
                  mb: 4,
                  p: 3,
                  bgcolor: 'grey.50',
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 500 }}>
                    Your QR Code
                  </Typography>
                  <Box sx={{ 
                    display: 'inline-block',
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Zoom in={true} timeout={600}>
                      {qrCodeImages[userId] ? (
                        <img
                          src={qrCodeImages[userId]}
                          alt="User QR Code"
                          style={{
                            width: '280px',
                            height: '280px',
                            maxWidth: '100%',
                          }}
                        />
                      ) : qrData?.qrImage?.data ? (
                        <img
                          src={`data:${qrData.qrImage.type};base64,${qrData.qrImage.data}`}
                          alt="User QR Code"
                          style={{
                            width: '280px',
                            height: '280px',
                            maxWidth: '100%',
                          }}
                        />
                      ) : (
                        <Box sx={{ width: 280, height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CircularProgress />
                        </Box>
                      )}
                    </Zoom>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.85rem' }}>
                    Scan this code to access your profile or login
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon width={20} height={20} />}
                    onClick={handleDownload}
                    size="large"
                    sx={{
                      minWidth: '160px',
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Download QR
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PrinterIcon width={20} height={20} />}
                    onClick={handlePrint}
                    size="large"
                    sx={{
                      minWidth: '160px',
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      }
                    }}
                  >
                    Print QR
                  </Button>
                  {qrData.profileUrl && (
                    <Button
                      variant="outlined"
                      startIcon={<ShareIcon width={20} height={20} />}
                      onClick={handleShare}
                      size="large"
                      sx={{
                        minWidth: '160px',
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                        }
                      }}
                    >
                      Share Profile
                    </Button>
                  )}
                </Box>
              </Box>
            </Fade>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          pt: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Button 
            onClick={handleClose} 
            variant="contained"
            size="large"
            sx={{
              minWidth: '120px',
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserQRCode;